import { AfterViewInit, Directive, ElementRef, forwardRef, HostBinding, inject, Input, OnDestroy } from '@angular/core';

import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yBooleanInput, A11yRole } from '../interfaces/a11y.interfaces';
import { A11yFocusService } from '../services/a11y-focus.service';
import { normalizeToken, toBoolean, toRequiredAriaBoolean } from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

const A11Y_ALERT_ROLES = ['alert', 'status'] as const;
const A11Y_ALERT_POLITENESS = ['polite', 'assertive', 'off'] as const;

/**
 * Directive for inline alert and status messages.
 *
 * Sets `role="alert"` by default with `aria-live="assertive"` and `aria-atomic="true"`.
 * Use `khA11yAlert="status"` for non-urgent informational messages.
 *
 * Set `[khA11yAlertTrapFocus]="true"` to capture and trap focus within the alert
 * until it is dismissed (destroyed). Use this for alerts with close buttons that
 * require user acknowledgement.
 */
@Directive({
  selector: '[khA11yAlert]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yAlertDirective) }]
})
export class A11yAlertDirective extends A11yLabelDirective implements A11yRole, AfterViewInit, OnDestroy {
  private readonly _elementRef = inject(ElementRef);
  private readonly _a11yFocusService = inject(A11yFocusService);

  @Input('khA11yAlert') public roleType: 'alert' | 'status' | '' = 'alert';
  @Input() public khA11yAlertPoliteness: 'polite' | 'assertive' | 'off' | null = null;
  @Input() public khA11yAlertAtomic: A11yBooleanInput = true;
  @Input() public khA11yAlertTrapFocus: A11yBooleanInput = false;

  @HostBinding('attr.role')
  public get role(): 'alert' | 'status' {
    return normalizeToken(this.roleType, A11Y_ALERT_ROLES, 'alert') ?? 'alert';
  }

  @HostBinding('attr.aria-live')
  public get ariaLive(): 'polite' | 'assertive' | 'off' {
    if (this.khA11yAlertPoliteness) {
      return normalizeToken(this.khA11yAlertPoliteness, A11Y_ALERT_POLITENESS, 'assertive') ?? 'assertive';
    }

    // Default: alert is assertive, status is polite
    return this.role === 'alert' ? 'assertive' : 'polite';
  }

  @HostBinding('attr.aria-atomic')
  public get ariaAtomic(): 'true' | 'false' {
    return toRequiredAriaBoolean(this.khA11yAlertAtomic);
  }

  public ngAfterViewInit(): void {
    if (!toBoolean(this.khA11yAlertTrapFocus)) {
      return;
    }

    const element = this._elementRef.nativeElement as HTMLElement;

    this._a11yFocusService.capturePreviousFocus();
    this._a11yFocusService.trapFocus(element);

    requestAnimationFrame(() => {
      this._a11yFocusService.focusInitialElement(element);
    });
  }

  public ngOnDestroy(): void {
    if (!toBoolean(this.khA11yAlertTrapFocus)) {
      return;
    }

    const element = this._elementRef.nativeElement as HTMLElement;

    this._a11yFocusService.cleanup(element);
    this._a11yFocusService.restoreFocus();
  }
}
